import { useEffect, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { SectionHeader } from '@factorialco/f0-react/dist/experimental'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  JoinOnlineJobPostingDocument,
  JoinOrganicChannelsDocument,
  JoinOrganicChannelsMutation,
} from 'generated/resources/graphql'

type IntegrationsJoinOrganicChannelDetails = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        NonNullable<JoinOrganicChannelsMutation['integrationsJoinMutations']>['listIntegrationsjoinOrganicChannel']
      >['organicChannel']
    >['channels']
  >[0]
>

import Callout from 'modules/core/components/Callout'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import { JobPostingLayout } from 'modules/ats/components/job_postings/JobPostingLayout'

import { useJobDetailsContext } from '../JobsPage/JobDetails'
import { JobPostingStandardPortalDetails } from './JobPostingStandardPortalDetails'
import { JobPostingStandardPortalDetailsSkeleton } from './JobPostingStandardPortalDetailsSkeleton'
import { UnpublishedJobNotice } from './UnpublishedJobNotice'

export const JobPostingStandardPortals = () => {
  const [organicChannels, setOrganicChannels] = useState<IntegrationsJoinOrganicChannelDetails[]>(
    []
  )
  const { access, company } = useCurrentSession()
  const { job } = useJobDetailsContext()
  const localStorageKey = LOCAL_STORAGE_KEYS.integrationsJoinHideStandarBoardCallout
  const [calloutRead, setCalloutRead] = useLocalStorage<boolean>(localStorageKey)
  const { data, loading: loadingJoinJob } = useQuery(JoinOnlineJobPostingDocument, {
    variables: { companyId: company.id, atsJobPostingIds: [job.id] },
  })
  const joinJobPosting = data?.integrationsJoin.jobPostingsConnection.nodes[0]

  const [fetchOrganicChannels, { loading: fetchingChannels }] = useMutation(
    JoinOrganicChannelsDocument,
    {
      extractorFn: (response) =>
        response.integrationsJoinMutations.listIntegrationsjoinOrganicChannel,
    }
  )

  const fetchChannels = async () => {
    const result = await fetchOrganicChannels({
      variables: { accessId: access.id, atsJobPostingId: job.id },
    })
    const handled = handleResult(result, () => undefined)

    if (handled.success) {
      setOrganicChannels(handled.data.organicChannel?.channels || [])
    }
  }

  useEffect(() => {
    if (!joinJobPosting?.createdInJoin) return

    fetchChannels()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinJobPosting])

  const isLoading = loadingJoinJob || fetchingChannels

  return (
    <JobPostingLayout job={job}>
      <Box gap="s24" width="full">
        {!isLoading && !organicChannels.length ? (
          <UnpublishedJobNotice />
        ) : (
          <>
            <SectionHeader
              title={i18n.t('ats.job_details.standard_portals.title')}
              description={i18n.t('ats.job_details.standard_portals.description')}
              separator="bottom"
            />
            <Box gap="s24">
              {!calloutRead && !!organicChannels.length && (
                <Callout
                  compact
                  type="info"
                  onRemove={() => setCalloutRead(true)}
                  description={i18n.t('ats.job_details.standard_portals.callout')}
                />
              )}
              <Box width="full" gap="s12">
                {isLoading
                  ? [...Array(6)].map((_, index) => (
                      <JobPostingStandardPortalDetailsSkeleton key={index} />
                    ))
                  : organicChannels.map((channel) => (
                      <JobPostingStandardPortalDetails key={channel.slug} channel={channel} />
                    ))}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </JobPostingLayout>
  )
}
